/** /tags/ — tenant-scoped tag catalog (list + CRUD; soft-delete).
 *
 * Hand-authored wrapper. The detail response embeds the category's effective
 * schema (`category.schema`) so the data editor can build itself, and `data` is
 * schema-ordered with `x-multilingual` fields folded to `{iso: value}`.
 */
import type { TagSchema } from '@/features/tags/schema/vocabulary';

import { apiDelete, apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { NamedRef, TenantRef } from './tours';
import type { PaginatedResponse } from './users';

export interface TagCategoryBrief {
  id: number;
  code: string;
  name: string;
  /** Whether this category's tags carry a meaningful `custom_id`. The write path
   *  refuses one otherwise, so this gates the External ID field everywhere. */
  external_identity: boolean;
}

export interface TagCategoryEmbed extends TagCategoryBrief {
  is_multitag: boolean;
  keyfield: string;
  schema: TagSchema | null;
}

export interface UserBrief {
  id: number;
  email: string | null;
  name: string | null;
}

export interface VehicleBrief {
  id: number;
  name: string;
}

export interface TagListItem {
  id: number;
  tenant: TenantRef | null;
  category: TagCategoryBrief;
  name: string;
  /** The source system's id for the entity this tag mirrors. Non-null only on an
   *  `external_identity` category, where it — not `name` — is the tag's identity. */
  custom_id: string | null;
  user: UserBrief | null;
  vehicle: VehicleBrief | null;
  /** Soft-deleted (only ever true when surfaced via an `is_deleted` filter). */
  is_deleted: boolean;
  created_ts: string;
  updated_ts: string;
}

export interface TagDetail {
  id: number;
  tenant: TenantRef | null;
  category: TagCategoryEmbed;
  name: string;
  custom_id: string | null;
  user: UserBrief | null;
  vehicle: VehicleBrief | null;
  data: Record<string, unknown>;
  atag: NamedRef[];
  /** Translation bindings touching this tag's data (metaplan A6): editor metadata, never content. */
  translation_bindings: TranslationBinding[];
  created_ts: string;
  updated_ts: string;
}

/** One translation binding (metaplan A6, AIE §8): the TARGET leaf is the machine translation
 *  of the ORIGIN leaf and is re-translated when the origin changes. `role` is this tag's side. */
export interface TranslationBinding {
  id: number;
  role: 'origin' | 'target';
  /** `<model>:<pk>:<field>:<JSON pointer>@<lang>` */
  origin: string;
  target: string;
  origin_label: string;
  target_label: string;
  origin_object: { model: string; id: number; live: boolean } | null;
  target_object: { model: string; id: number; live: boolean } | null;
  /** JSON pointer of the multilingual attribute, e.g. `/meeting_point`. */
  origin_path: string;
  target_path: string;
  origin_lang: string;
  target_lang: string;
  status: 'active' | 'error' | 'paused' | 'orphaned';
  params: Record<string, unknown>;
  last_synced_ts: string | null;
  last_error: string;
}

export interface ListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  tour_operator_id?: number | string;
  category?: number;
  user?: number;
  /** JSON-encoded WireNode[] (already stringified by the page). */
  filters?: string;
  /** Override the endpoint's own row-total mode. Omit to use the server
   *  default — that omission is what keeps a cold first load free. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.timeout) usp.set('timeout', params.timeout);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) usp.set('tour_operator_id', String(params.tour_operator_id));
  if (params.category !== undefined) usp.set('category', String(params.category));
  if (params.user !== undefined) usp.set('user', String(params.user));
  if (params.filters) usp.set('filters', params.filters);
  if (params.count_mode) usp.set('count_mode', params.count_mode);
  if (params.count_only) usp.set('count_only', '1');
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export interface TagCreateInput {
  tour_operator: number | null;
  category: number;
  name: string;
  /** Accepted only when the category declares `external_identity`. */
  custom_id?: string | null;
  data?: Record<string, unknown>;
  user?: number | null;
  vehicle?: number | null;
  atag?: { id: number }[];
}

export interface TagWriteInput {
  tour_operator?: number | null;
  category?: number;
  name?: string;
  custom_id?: string | null;
  data?: Record<string, unknown>;
  user?: number | null;
  vehicle?: number | null;
  atag?: { id: number }[];
  /** PATCH only: unbind these translation bindings (targets in this tag) with the edit. */
  translation_unbind?: number[];
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TagListItem>> {
  return apiGet<PaginatedResponse<TagListItem>>(`/tags/${toQuery(params)}`, extendedListConfig(params.timeout));
}

/** Total only, for the grid's on-demand row count (computed in Celery). */
export function count(params: ListParams = {}): Promise<CountOnlyResult> {
  return apiGet<CountOnlyResult>(
    `/tags/${toQuery({ ...params, count_only: true, count_mode: 'async' })}`,
  );
}

export function retrieve(id: number): Promise<TagDetail> {
  return apiGet<TagDetail>(`/tags/${id}/`);
}

export function create(body: TagCreateInput): Promise<TagDetail> {
  return apiPost<TagDetail>('/tags/', body);
}

export function update(id: number, body: TagWriteInput): Promise<TagDetail> {
  return apiPatch<TagDetail>(`/tags/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/tags/${id}/`);
}
